import { useEffect } from "react";

interface PageMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  ogUrl?: string;
  jsonLd?: Record<string, unknown>[];
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = "website",
  ogImage,
  ogUrl,
  jsonLd,
}: PageMetaProps) {
  useEffect(() => {
    const originalTitle = document.title;

    if (title) {
      document.title = title;
    }

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    if (ogTitle) setMeta("og:title", ogTitle, true);
    if (ogDescription) setMeta("og:description", ogDescription, true);
    if (ogType) setMeta("og:type", ogType, true);
    if (ogImage) setMeta("og:image", ogImage, true);
    if (ogUrl) setMeta("og:url", ogUrl, true);

    const removeCanonical = () => {
      document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    };

    if (canonicalUrl) {
      removeCanonical();
      const link = document.createElement("link");
      link.rel = "canonical";
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    const scriptIds: string[] = [];
    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((schema, index) => {
        const id = `jsonld-${index}`;
        let script = document.getElementById(id) as HTMLScriptElement | null;
        if (!script) {
          script = document.createElement("script");
          script.type = "application/ld+json";
          script.id = id;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(schema);
        scriptIds.push(id);
      });
    }

    return () => {
      document.title = originalTitle;
      scriptIds.forEach((id) => {
        const s = document.getElementById(id);
        if (s) s.remove();
      });
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogImage, ogUrl, jsonLd]);

  return null;
}